import React, { useState, useEffect } from 'react';
import { useAuth } from '../lib/auth';
import { api } from '../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Truck } from 'lucide-react';

const Dispatch = () => {
  const { hasRole } = useAuth();
  const [dispatches, setDispatches] = useState([]);
  const [parties, setParties] = useState([]);
  const [coilIns, setCoilIns] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    party_id: '',
    coil_in_id: '',
    dispatch_datetime: new Date().toISOString().slice(0, 16),
    transport_notes: ''
  });

  const canCreate = hasRole(['ADMIN', 'OWNER', 'MUNIM']);

  useEffect(() => {
    fetchDispatches();
    fetchParties();
    fetchCoilIns();
  }, []);

  const fetchDispatches = async () => {
    try {
      const response = await api.dispatches.list();
      setDispatches(response.data);
    } catch (error) {
      toast.error('Failed to fetch dispatches');
    }
  };

  const fetchParties = async () => {
    try {
      const response = await api.parties.list({ is_active: true });
      setParties(response.data);
    } catch (error) {
      toast.error('Failed to fetch parties');
    }
  };

  const fetchCoilIns = async () => {
    try {
      const response = await api.coilIns.list({ is_active: true });
      setCoilIns(response.data);
    } catch (error) {
      toast.error('Failed to fetch coil entries');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.dispatches.create({
        party_id: formData.party_id,
        coil_in_id: formData.coil_in_id,
        output_lot_ids: [],
        scrap_entry_ids: [],
        dispatch_datetime: new Date(formData.dispatch_datetime).toISOString(),
        transport_notes: formData.transport_notes || null,
        photos: []
      });
      toast.success('Dispatch created successfully');
      setDialogOpen(false);
      resetForm();
      fetchDispatches();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create dispatch');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      party_id: '',
      coil_in_id: '',
      dispatch_datetime: new Date().toISOString().slice(0, 16),
      transport_notes: ''
    });
  };

  return (
    <div className="min-h-screen bg-industrial-bg text-white p-6 space-y-6" data-testid="dispatch-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-industrial-border pb-4">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight uppercase">Dispatch Management</h1>
          <p className="text-slate-400 mt-2 font-mono text-xs uppercase">Track outbound deliveries</p>
        </div>
        
        {canCreate && (
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <button 
                onClick={resetForm} 
                className="bg-accent text-white font-mono uppercase tracking-wider font-bold rounded-none border-b-2 border-black/30 hover:bg-accent-hover h-11 px-4 flex items-center justify-center gap-1.5 transition-colors"
                data-testid="create-dispatch-button"
              >
                <Plus className="h-4 w-4" /> CREATE DISPATCH
              </button>
            </DialogTrigger>
            
            <DialogContent className="bg-industrial-s1 border border-industrial-border text-white rounded-none max-w-2xl p-6">
              <DialogHeader>
                <DialogTitle className="font-heading text-lg font-bold uppercase tracking-wide border-b border-industrial-border pb-2 text-white">Create Dispatch</DialogTitle>
              </DialogHeader>
              
              <form onSubmit={handleSubmit} className="space-y-4 text-xs mt-2">
                <div>
                  <label className="block uppercase text-[10px] tracking-wider text-slate-400 font-sans mb-1 font-semibold font-bold">Party *</label>
                  <select 
                    value={formData.party_id} 
                    onChange={(e) => setFormData({ ...formData, party_id: e.target.value })}
                    className="flex h-10 w-full items-center rounded-none border border-industrial-border bg-industrial-s2 px-3 py-2 text-xs font-mono text-white focus:outline-none focus:border-accent"
                    data-testid="dispatch-party-select"
                    required
                  >
                    <option value="">Select party</option>
                    {parties.map(party => (
                      <option key={party.id} value={party.id}>{party.name}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block uppercase text-[10px] tracking-wider text-slate-400 font-sans mb-1 font-semibold font-bold">Coil *</label>
                  <select 
                    value={formData.coil_in_id} 
                    onChange={(e) => setFormData({ ...formData, coil_in_id: e.target.value })}
                    className="flex h-10 w-full items-center rounded-none border border-industrial-border bg-industrial-s2 px-3 py-2 text-xs font-mono text-white focus:outline-none focus:border-accent"
                    required
                  >
                    <option value="">Select coil</option>
                    {coilIns.map(coil => (
                      <option key={coil.id} value={coil.id}>
                        {coil.party_name} - {new Date(coil.inward_datetime).toLocaleDateString()}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label htmlFor="dispatch_datetime" className="block uppercase text-[10px] tracking-wider text-slate-400 font-sans mb-1 font-semibold font-bold">Dispatch DateTime *</label>
                  <input
                    id="dispatch_datetime"
                    type="datetime-local"
                    className="w-full h-10 px-3 bg-industrial-s2 border border-industrial-border font-mono text-white rounded-none focus:border-accent focus:outline-none"
                    value={formData.dispatch_datetime}
                    onChange={(e) => setFormData({ ...formData, dispatch_datetime: e.target.value })}
                    required
                  />
                </div>
                
                <div>
                  <label htmlFor="transport_notes" className="block uppercase text-[10px] tracking-wider text-slate-400 font-sans mb-1 font-semibold font-bold">Transport Notes</label>
                  <textarea
                    id="transport_notes"
                    placeholder="Truck No, Driver details, etc."
                    className="w-full min-h-[80px] p-3 bg-industrial-s2 border border-industrial-border font-mono text-white rounded-none focus:border-accent focus:outline-none"
                    value={formData.transport_notes}
                    onChange={(e) => setFormData({ ...formData, transport_notes: e.target.value })}
                  />
                </div>
                
                <div className="flex gap-2 justify-end border-t border-industrial-border pt-4 mt-2">
                  <button 
                    type="button" 
                    onClick={() => setDialogOpen(false)}
                    className="bg-industrial-s2 border border-industrial-border hover:bg-neutral-800 text-white rounded-none font-mono text-xs uppercase tracking-wider h-11 px-4 transition-colors"
                  >
                    CANCEL
                  </button>
                  <button 
                    type="submit" 
                    disabled={loading} 
                    className="bg-accent text-white font-mono uppercase tracking-wider font-bold rounded-none border-b-2 border-black/30 hover:bg-accent-hover h-11 px-6 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    data-testid="submit-dispatch"
                  >
                    {loading ? 'CREATING...' : 'CREATE DISPATCH'}
                  </button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="bg-industrial-s1 border border-industrial-border p-6 space-y-4">
        <div className="flex items-center gap-2 border-b border-industrial-border pb-3 mb-4">
          <Truck className="h-5 w-5 text-slate-400" />
          <h2 className="text-xs uppercase font-sans tracking-wider text-slate-400 font-semibold">Dispatch Records</h2>
        </div>
        
        {dispatches.length === 0 ? (
          <div className="text-center py-8 text-slate-500 font-mono text-xs uppercase">No dispatches yet</div>
        ) : (
          <div className="space-y-3">
            {dispatches.map(dispatch => (
              <div key={dispatch.id} className="border border-industrial-border bg-industrial-s2/20 p-4 font-mono text-xs hover:bg-industrial-s2/30 transition-colors">
                <div className="flex flex-col md:flex-row md:items-start justify-between gap-2 border-b border-industrial-border/30 pb-2 mb-2">
                  <div className="flex-1">
                    <p className="font-sans text-sm font-bold text-white uppercase">{dispatch.party_name}</p>
                    <p className="text-slate-400 mt-1">DISPATCHED ON: {new Date(dispatch.dispatch_datetime).toLocaleString()}</p>
                  </div>
                </div>
                {dispatch.transport_notes && (
                  <p className="text-slate-300 bg-industrial-s2 p-2.5 border border-industrial-border/60 mb-2 max-w-full font-mono">{dispatch.transport_notes}</p>
                )}
                <p className="text-[10px] text-slate-500">
                  SYSTEM LOGGED AT: {new Date(dispatch.created_at).toLocaleString()}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Dispatch;
